import React from 'react';

import { DateRangeFilter } from '@/components/ui/filters/DateRangeFilter';
import type { DateRangeValue } from '@/components/ui/filters/DateRangeFilter';
import { formatDateToYYYYMMDD, getMinDateByMonths } from '@/utils';
import { Selector } from '@/components/ui/inputs/selector';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { FilterDropdown } from '@/components/ui/filters/FilterDropdown';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import Table from '@/components/ui/tables/Table';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { useTranslation } from '@/i18n';
import type { EmployeeDocument } from '@/api/domains/employeeDocument';


interface MyDocumentsListFilters {
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
  selectedFolder: string | undefined;
  setSelectedFolder: (value: string | undefined) => void;
  selectedDocumentType: string | undefined;
  setSelectedDocumentType: (value: string | undefined) => void;
  folderOptions: SelectorOption[];
  documentTypeOptions: SelectorOption[];
}

interface MyDocumentsListTable {
  columns: TableColumn<EmployeeDocument>[];
  actions: TableAction<EmployeeDocument>[];
  rows: EmployeeDocument[];
  total: number;
  loading: boolean;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  onSearch: (text: string) => void;
}

interface MyDocumentsListProps {
  filters: MyDocumentsListFilters;
  table: MyDocumentsListTable;
}

export const MyDocumentsList: React.FC<MyDocumentsListProps> = ({
  filters,
  table,
}) => {
  const { t: translate } = useTranslation();

  return (
    <div className="flex flex-col gap-6" data-testid="mis-documentos-documentos-view">
      {/* Filter bar */}
      <div className="flex flex-wrap gap-4 items-end">
        <DateRangeFilter
          value={filters.dateRange}
          onChange={filters.setDateRange}
          minDate={getMinDateByMonths()}
          maxDate={formatDateToYYYYMMDD(new Date())}
        />
        <FilterDropdown
          renderExtraFilters={(close) => (
            <div className="flex flex-col gap-4">
              <Selector
                label={translate('employee:misDocumentos.filters.folder')}
                options={filters.folderOptions}
                value={filters.selectedFolder ?? ''}
                onChange={(value) => {
                  const resolved = Array.isArray(value) ? value[0] : value;
                  filters.setSelectedFolder(resolved ?? undefined);
                }}
                data-testid="mis-documentos-folder-selector"
              />
              <Selector
                label={translate('employee:misDocumentos.filters.documentType')}
                options={filters.documentTypeOptions}
                value={filters.selectedDocumentType ?? ''}
                onChange={(value) => {
                  const resolved = Array.isArray(value) ? value[0] : value;
                  filters.setSelectedDocumentType(resolved ?? undefined);
                }}
                data-testid="mis-documentos-document-type-selector"
              />
              <PrimaryButton
                onClick={close}
                data-testid="mis-documentos-search-button"
              >
                {translate('employee:misDocumentos.filters.search')}
              </PrimaryButton>
            </div>
          )}
        />
      </div>

      {/* Table */}
      <Table
        columns={table.columns}
        data={table.rows}
        totalRows={table.total}
        searchable
        onSearch={table.onSearch}
        serverSideSort
        onSortChange={table.onSortChange}
        pagination={{
          page: table.page,
          pageSize: table.pageSize,
          total: table.total,
          onChange: table.setPage,
          onPageSizeChange: table.setPageSize,
        }}
        isLoading={table.loading}
        actions={table.actions}
        data-testid="mis-documentos-table"
      />
    </div>
  );
};

export default MyDocumentsList;
